/**
 * Phase 8: Admin Build Dashboard
 * Provides visibility and control over SSG pre-render pipeline
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Zap, 
  Database,
  TrendingUp,
  AlertTriangle
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface BuildStatus {
  totalModules: number;
  prerenderedModules: number;
  failedModules: string[];
  lastBuildTime: string | null;
  buildDuration: number | null;
  storageSize: number | null;
}

const BuildDashboard: React.FC = () => {
  const [buildStatus, setBuildStatus] = useState<BuildStatus>({
    totalModules: 927,
    prerenderedModules: 0,
    failedModules: [],
    lastBuildTime: null,
    buildDuration: null,
    storageSize: null,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRebuilding, setIsRebuilding] = useState(false);

  // Fetch build status from database
  const fetchBuildStatus = async () => {
    setIsRefreshing(true);
    try {
      // Query total modules
      const { count: totalModules } = await supabase
        .from('course_modules')
        .select('*', { count: 'exact', head: true });

      // Query module_content to check enhanced content status
      const { data: enhancedContent } = await supabase
        .from('module_content')
        .select('module_id, updated_at')
        .order('updated_at', { ascending: false })
        .limit(1);

      // TODO: Query actual prerender status from storage bucket once implemented
      // For now, assume all modules with enhanced content are pre-rendered
      const { count: prerenderedCount } = await supabase
        .from('module_content')
        .select('*', { count: 'exact', head: true });

      setBuildStatus({
        totalModules: totalModules || 927,
        prerenderedModules: prerenderedCount || 0,
        failedModules: [],
        lastBuildTime: enhancedContent?.[0]?.updated_at || null,
        buildDuration: null,
        storageSize: null,
      });

      toast.success('Build status refreshed');
    } catch (error) {
      console.error('Failed to fetch build status:', error);
      toast.error('Failed to refresh build status');
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBuildStatus();
  }, []);

  const handleForceRebuild = async () => {
    setIsRebuilding(true);
    try {
      toast.info('Manual rebuild is not yet implemented. Run `tsx scripts/prerender.ts` manually.');
      // TODO: Implement Edge Function to trigger rebuild
      // await supabase.functions.invoke('trigger-rebuild', { body: {} });
    } catch (error) {
      console.error('Rebuild failed:', error);
      toast.error('Rebuild failed');
    } finally {
      setIsRebuilding(false);
    }
  };

  const completionPercentage = (buildStatus.prerenderedModules / buildStatus.totalModules) * 100;
  const freshnessMinutes = buildStatus.lastBuildTime 
    ? Math.floor((Date.now() - new Date(buildStatus.lastBuildTime).getTime()) / 60000)
    : null;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">SSG Build Dashboard</h1>
          <p className="text-muted-foreground">Monitor and control pre-render pipeline</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchBuildStatus}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={handleForceRebuild}
            disabled={isRebuilding}
          >
            <Zap className="h-4 w-4 mr-2" />
            Force Rebuild
          </Button>
        </div>
      </div>

      {/* Build Status Overview */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Modules</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{buildStatus.totalModules}</div>
            <p className="text-xs text-muted-foreground">Across all courses</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pre-rendered</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{buildStatus.prerenderedModules}</div>
            <p className="text-xs text-muted-foreground">{completionPercentage.toFixed(1)}% complete</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{buildStatus.failedModules.length}</div>
            <p className="text-xs text-muted-foreground">
              {buildStatus.failedModules.length > 0 ? 'Requires attention' : 'All passing'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Build</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {freshnessMinutes !== null ? `${freshnessMinutes}m` : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              {buildStatus.lastBuildTime 
                ? formatDistanceToNow(new Date(buildStatus.lastBuildTime), { addSuffix: true })
                : 'Never built'
              }
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Build Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Build Progress</CardTitle>
          <CardDescription>
            Pre-rendering status for all {buildStatus.totalModules} modules
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Completion</span>
              <span className="font-medium">{completionPercentage.toFixed(1)}%</span>
            </div>
            <Progress value={completionPercentage} className="h-2" />
          </div>

          {completionPercentage < 100 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {buildStatus.totalModules - buildStatus.prerenderedModules} modules need pre-rendering.
                Run <code className="px-1 py-0.5 bg-muted rounded">tsx scripts/prerender.ts</code> to generate static HTML.
              </AlertDescription>
            </Alert>
          )}

          {completionPercentage === 100 && (
            <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800 dark:text-green-200">
                All modules pre-rendered successfully! Platform is running at peak performance.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Content Freshness */}
      <Card>
        <CardHeader>
          <CardTitle>Content Freshness</CardTitle>
          <CardDescription>
            How recently static HTML was generated
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm">Cache Status</span>
              <Badge variant={freshnessMinutes && freshnessMinutes < 120 ? 'default' : 'secondary'}>
                {freshnessMinutes && freshnessMinutes < 60 ? 'Fresh' : 
                 freshnessMinutes && freshnessMinutes < 1440 ? 'Stale' : 'Very Stale'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Last Update</span>
              <span className="text-sm font-medium">
                {buildStatus.lastBuildTime 
                  ? new Date(buildStatus.lastBuildTime).toLocaleString()
                  : 'Never'
                }
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm">Build Duration</span>
              <span className="text-sm font-medium">
                {buildStatus.buildDuration ? `${buildStatus.buildDuration}s` : 'Unknown'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Failed Modules */}
      {buildStatus.failedModules.length > 0 && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">Failed Modules</CardTitle>
            <CardDescription>
              These modules failed to pre-render and require manual attention
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {buildStatus.failedModules.map((moduleId) => (
                <div 
                  key={moduleId}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <code className="text-sm">{moduleId}</code>
                  <Button variant="outline" size="sm">
                    Retry
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Next Steps */}
      <Card>
        <CardHeader>
          <CardTitle>Next Steps</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Alert>
            <TrendingUp className="h-4 w-4" />
            <AlertDescription>
              <strong>To complete SSG setup:</strong>
              <ol className="list-decimal list-inside mt-2 space-y-1 text-sm">
                <li>Run <code className="px-1 py-0.5 bg-muted rounded">tsx scripts/prerender.ts</code> to generate 927 HTML files (~8-12 min)</li>
                <li>Run <code className="px-1 py-0.5 bg-muted rounded">tsx scripts/uploadStaticHTML.ts</code> to upload to CDN (~3-5 min)</li>
                <li>Deploy - Edge Function will auto-serve static HTML with <strong>&lt;200ms</strong> response time</li>
              </ol>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
};

export default BuildDashboard;
